import React from 'react';
import {
  TTypeConstructor,
  TRecord,
  TBoolean,
  TText,
  TListOfElement,
  functionOf,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {
  Component,
  TypeImplementation,
  Plugins,
  withSystem,
  Variable,
} from '@packages/systems/wfdl';
import {getElementWithAncestors} from '@packages/utilities/platform';
import {
  isLocalesItem,
  isLocalesWrapper,
} from '@packages/utilities/elementTypeUtils';
import {AUTO_REDIRECT_OPT_OUT_PARAM} from '@packages/systems/localization-constants';
import {
  localesListItemVariable,
  localesListActiveItemVariable,
  type LocaleItemContext,
} from '@packages/shared/constants/Render';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';

export {
  localesListItemVariable,
  localesListActiveItemVariable,
  LocaleItemContext,
};

export const isLocalesListItemVariable = Variable.equals(
  localesListItemVariable
);

export const isLocalesListActiveItemVariable = Variable.equals(
  localesListActiveItemVariable
);

export const isLocalesVariable = (variable: Variable.VariableType) =>
  isLocalesListItemVariable(variable) ||
  isLocalesListActiveItemVariable(variable);

export const LocalesTagType = TTypeConstructor(
  qualify(Plugins.name('Localization'), typeName('Tag'))
);

export const TLocalesTag = withSystem({label: 'Locale code'}, LocalesTagType);

export const LocalesLanguageCodeType = TTypeConstructor(
  qualify(Plugins.name('Localization'), typeName('LanguageCode'))
);

export const TLocalesLanguageCode = withSystem(
  {label: 'Language code'},
  LocalesLanguageCodeType
);

export const LocalesCountryCodeType = TTypeConstructor(
  qualify(Plugins.name('Localization'), typeName('CountryCode'))
);

export const TLocalesCountryCode = withSystem(
  {label: 'Country code'},
  LocalesCountryCodeType
);

export const LocalesDisplayNameType = TTypeConstructor(
  qualify(Plugins.name('Localization'), typeName('DisplayName'))
);

export const TLocalesDisplayName = withSystem(
  {label: 'Display name'},
  LocalesDisplayNameType
);

export const TLocalesDisplayImageId = withSystem(
  {label: 'Display image'},
  TTypeConstructor(
    qualify(Plugins.name('Localization'), typeName('DisplayImageId'))
  )
);

export const TLocalesSubdirectory = withSystem(
  {label: 'Subdirectory'},
  TTypeConstructor(
    qualify(Plugins.name('Localization'), typeName('Subdirectory'))
  )
);

export const TLocalesActive = withSystem({label: 'Active'}, TBoolean);

export const TLocalesPageId = withSystem(
  {label: 'Page'},
  TTypeConstructor(qualify(Plugins.name('Localization'), typeName('PageId')))
);

export const TagType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Localization'), iconName('ID'))
  ),
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    const literal = ContextualExpressions.getLiteral(stripAnn(expr));
    const literalValue = literal?.value;
    const value = typeof literalValue === 'string' ? literalValue : '';
    return <span>{value || ''}</span>;
  },
});

export const DisplayNameType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Text'))
  ),
});

export const DisplayImageIdType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Image'))
  ),
});

export const SubdirectoryType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Link'))
  ),
});

export const PageIdType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Link'))
  ),
});

export const ActiveType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Boolean'))
  ),
});

export const LanguageCodeType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Localization'), iconName('Translate'))
  ),
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    const literal = ContextualExpressions.getLiteral(stripAnn(expr));
    const literalValue = literal?.value;
    const value = typeof literalValue === 'string' ? literalValue : '';
    return <span>{value || ''}</span>;
  },
});

export const CountryCodeType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Localization'), iconName('Location'))
  ),
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    const literal = ContextualExpressions.getLiteral(stripAnn(expr));
    const literalValue = literal?.value;
    const value = typeof literalValue === 'string' ? literalValue : '';
    return <span>{value || ''}</span>;
  },
});

export const TLocalesActiveItem = withSystem(
  {label: 'Current locale'},
  TRecord({
    tag: TLocalesTag,
    languageCode: TLocalesLanguageCode,
    countryCode: TLocalesCountryCode,
    displayName: TLocalesDisplayName,
    displayImageId: TLocalesDisplayImageId,
    subdirectory: TLocalesSubdirectory,
    pageId: TLocalesPageId,
  })
);

export const TLocalesItem = withSystem(
  {label: 'Locales list item'},
  TRecord({
    tag: TLocalesTag,
    languageCode: TLocalesLanguageCode,
    countryCode: TLocalesCountryCode,
    displayName: TLocalesDisplayName,
    displayImageId: TLocalesDisplayImageId,
    subdirectory: TLocalesSubdirectory,
    pageId: TLocalesPageId,
    active: TLocalesActive,
  })
);

export function getLocalesItemType(
  elementId: string,
  currentComponent: Component.ComponentType
) {
  const rootElement = Component.getRender(currentComponent);
  const ancestorElements = getElementWithAncestors(rootElement)(elementId);

  return ancestorElements.find(isLocalesItem) ? TLocalesItem : null;
}

export function getLocalesActiveItemType(
  elementId: string,
  currentComponent: Component.ComponentType
) {
  const rootElement = Component.getRender(currentComponent);
  const ancestorElements = getElementWithAncestors(rootElement)(elementId);

  return ancestorElements.find(isLocalesWrapper) ? TLocalesActiveItem : null;
}

export const tagToText = {
  envType: {
    dataType: functionOf(TLocalesTag, TText),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (tag: string) => tag,
} as const;

export const tagToListOfElements = {
  envType: {
    dataType: functionOf(TLocalesTag, TListOfElement),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (tag: string) => [
    {
      text: true,
      type: null,
      id: 'temp',
      children: [],
      data: {value: tag},
    },
  ],
} as const;

export const displayNameToText = {
  envType: {
    dataType: functionOf(TLocalesDisplayName, TText),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (displayName: string) => displayName,
} as const;

export const displayNameToListOfElements = {
  envType: {
    dataType: functionOf(TLocalesDisplayName, TListOfElement),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (displayName: string) => {
    return [
      {
        text: true,
        type: null,
        id: 'temp',
        children: [],
        data: {value: displayName},
      },
    ];
  },
} as const;

const TBasicLink = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('Link'))
);

type BasicExternalLinkValue = {
  url: string;
  mode: 'external';
  preload: 'none';
};

type BasicPageLinkValue = {
  pageId: string;
  mode: 'page';
  preload: 'none';
};

export const subdirectoryToBasicLink = {
  envType: {
    dataType: functionOf(TLocalesSubdirectory, TBasicLink),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (subdirectory: string): BasicExternalLinkValue => {
    const url =
      subdirectory === ''
        ? `/${AUTO_REDIRECT_OPT_OUT_PARAM}`
        : `/${subdirectory}`;

    return {
      url,
      mode: 'external',
      preload: 'none',
    };
  },
} as const;

export const pageIdToBasicLink = {
  envType: {
    dataType: functionOf(TLocalesPageId, TBasicLink),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (pageId: string): BasicPageLinkValue => {
    return {
      pageId,
      mode: 'page',
      preload: 'none',
    };
  },
} as const;

const TImage = TTypeConstructor(
  qualify(Plugins.name('Asset'), typeName('Image'))
);

export const displayImageIdToImage = {
  envType: {
    dataType: functionOf(TLocalesDisplayImageId, TImage),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (displayImageId?: string | null) => {
    return displayImageId ? {id: displayImageId} : null;
  },
} as const;

export const languageCodeToText = {
  envType: {
    dataType: functionOf(TLocalesLanguageCode, TText),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (languageCode: string) => languageCode,
} as const;

export const languageCodeToListOfElements = {
  envType: {
    dataType: functionOf(TLocalesLanguageCode, TListOfElement),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (languageCode: string) => [
    {
      text: true,
      type: null,
      id: 'temp',
      children: [],
      data: {value: languageCode},
    },
  ],
} as const;

export const countryCodeToText = {
  envType: {
    dataType: functionOf(TLocalesCountryCode, TText),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (countryCode: string) => countryCode,
} as const;

export const countryCodeToListOfElements = {
  envType: {
    dataType: functionOf(TLocalesCountryCode, TListOfElement),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (countryCode: string) => [
    {
      text: true,
      type: null,
      id: 'temp',
      children: [],
      data: {value: countryCode},
    },
  ],
} as const;
